import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { identifyUser, resetAnalyticsUser } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;
const GameContext = createContext(null);

/* ── Device fingerprint (persisted in localStorage) ── */
function getOrCreateDeviceId() {
  let id = localStorage.getItem("hujjah_device_id");
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    localStorage.setItem("hujjah_device_id", id);
  }
  return id;
}

export const GameProvider = ({ children }) => {
  const [session, setSession] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hujjah_session") || "null"); }
    catch { return null; }
  });
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hujjah_user") || "null"); }
    catch { return null; }
  });
  const [userToken, setUserToken] = useState(() => localStorage.getItem("hujjah_user_token") || null);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("hujjah_dark") === "true");
  const [deviceId] = useState(getOrCreateDeviceId);
  const [gameSettings, setGameSettings] = useState({ default_timer: 65, word_timers: { "300": 80, "600": 60, "900": 45 } });
  const [currentTurn, setCurrentTurn] = useState(() => parseInt(localStorage.getItem("hujjah_turn") || "1"));

  // ─── Per-team lifelines — persist across questions, reset only on new game ──
  const DEFAULT_LIFELINES = {
    1: { changeQuestion: true, extraTime: true, doublePoints: true },
    2: { changeQuestion: true, extraTime: true, doublePoints: true },
  };
  const [lifelinesState, setLifelinesState] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem("hujjah_session") || "null");
      if (s?.id) {
        const raw = localStorage.getItem(`lifelines_${s.id}`);
        if (raw) return JSON.parse(raw);
      }
    } catch {}
    return DEFAULT_LIFELINES;
  });
  const sessionIdRef = useRef(null);
  useEffect(() => { sessionIdRef.current = session?.id ?? null; }, [session]);

  const consumeLifeline = useCallback((team, key) => {
    setLifelinesState(prev => {
      const next = { ...prev, [team]: { ...prev[team], [key]: false } };
      const sid = sessionIdRef.current;
      if (sid) localStorage.setItem(`lifelines_${sid}`, JSON.stringify(next));
      return next;
    });
  }, []);

  // Central game state
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [remainingTime, setRemainingTime] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState(() => {
    // Load selected tiles from localStorage if session exists
    try {
      const s = JSON.parse(localStorage.getItem("hujjah_session") || "null");
      if (s?.id) {
        const raw = localStorage.getItem(`used_${s.id}`);
        return raw ? new Set(JSON.parse(raw)) : new Set();
      }
    } catch {}
    return new Set();
  });

  // ── Match manifest: maps slot_key → full question object, prepared before gameplay ──
  const [manifest, setManifest] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem("hujjah_session") || "null");
      if (s?.id) {
        const raw = localStorage.getItem(`manifest_${s.id}`);
        return raw ? JSON.parse(raw) : null;
      }
    } catch {}
    return null;
  });

  const saveManifest = useCallback((m, sessionId) => {
    setManifest(m);
    const sid = sessionId || sessionIdRef.current;
    if (m && sid) {
      try { localStorage.setItem(`manifest_${sid}`, JSON.stringify(m)); } catch {}
    } else if (!m && sid) {
      localStorage.removeItem(`manifest_${sid}`);
    }
  }, []);

  const [teamScores, setTeamScores] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem("hujjah_session") || "null");
      return { team1: s?.team1_score ?? 0, team2: s?.team2_score ?? 0 };
    } catch { return { team1: 0, team2: 0 }; }
  });

  // ─── Game Mode & Tournament ───────────────────────────────────────────────
  const [gameMode, setGameModeState] = useState(() => localStorage.getItem("hujjah_mode") || "standard");
  const [tournamentState, setTournamentState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hujjah_tournament") || "null"); } catch { return null; }
  });
  // ─── SOLO setup: { playerName, difficulty, target } ──────────────────────
  const [soloSetup, setSoloSetupState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hujjah_solo_setup") || "null"); } catch { return null; }
  });

  // Load settings on mount
  useEffect(() => {
    axios.get(`${API}/settings`).then(({ data }) => setGameSettings(data)).catch(() => {});
  }, []);

  // ── Silent user refresh on every app start ────────────────────────────────
  // Keeps subscription_type / game_credits in sync with DB even after
  // an interrupted payment flow or a stale localStorage.
  useEffect(() => {
    if (!userToken) return;
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${userToken}` } })
      .then(({ data }) => {
        setCurrentUser(data);
        localStorage.setItem("hujjah_user", JSON.stringify(data));
      })
      .catch(() => {}); // silent — network hiccup shouldn't affect the session
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pending Tamara payment watcher ───────────────────────────────────────
  // If the user navigated away from /payment/success before the order was
  // confirmed, this effect picks it up on the very next page load and
  // updates the user's premium status without requiring a logout.
  useEffect(() => {
    const orderId = localStorage.getItem("tamara_order_id");
    if (!orderId || !userToken) return;
    let active = true;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/tamara/verify/${orderId}`, {
          headers: { Authorization: `Bearer ${userToken}` },
        });
        if (!active) return;

        if (data.status === "paid") {
          localStorage.removeItem("tamara_order_id");
          const freshUser = data.user;
          if (freshUser) {
            setCurrentUser(freshUser);
            localStorage.setItem("hujjah_user", JSON.stringify(freshUser));
          } else {
            // Fallback: pull from /auth/me directly
            const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${userToken}` } });
            if (active) {
              setCurrentUser(me.data);
              localStorage.setItem("hujjah_user", JSON.stringify(me.data));
            }
          }
        } else if (["declined", "canceled", "expired", "failed"].includes(data.status)) {
          localStorage.removeItem("tamara_order_id");
        } else {
          // Still pending — keep polling every 4 s
          setTimeout(poll, 4000);
        }
      } catch { /* stop polling on error */ }
    };

    poll();
    return () => { active = false; };
  }, [userToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync teamScores when session changes
  useEffect(() => {
    if (session) {
      setTeamScores({ team1: session.team1_score ?? 0, team2: session.team2_score ?? 0 });
    }
  }, [session]);

  const markTileUsed = useCallback((tileKey) => {
    setSelectedQuestions(prev => {
      const next = new Set(prev);
      next.add(tileKey);
      if (session?.id) {
        localStorage.setItem(`used_${session.id}`, JSON.stringify([...next]));
      }
      return next;
    });
  }, [session]);

  const isTileUsed = useCallback((tileKey) => {
    return selectedQuestions.has(tileKey);
  }, [selectedQuestions]);

  // Restore (undo) a tile — removes it from used set so it reappears on board
  const restoreTile = useCallback((tileKey) => {
    setSelectedQuestions(prev => {
      const next = new Set(prev);
      next.delete(tileKey);
      if (session?.id) {
        localStorage.setItem(`used_${session.id}`, JSON.stringify([...next]));
      }
      return next;
    });
  }, [session]);

  const setGameMode = (mode) => {
    setGameModeState(mode);
    localStorage.setItem("hujjah_mode", mode);
  };

  const setTournament = (state) => {
    setTournamentState(state);
    if (state) localStorage.setItem("hujjah_tournament", JSON.stringify(state));
    else localStorage.removeItem("hujjah_tournament");
  };

  const setSoloSetup = (setup) => {
    setSoloSetupState(setup);
    if (setup) localStorage.setItem("hujjah_solo_setup", JSON.stringify(setup));
    else localStorage.removeItem("hujjah_solo_setup");
  };

  const updateTournament = (updater) => {
    setTournamentState(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      localStorage.setItem("hujjah_tournament", JSON.stringify(next));
      return next;
    });
  };

  const toggleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem("hujjah_dark", String(next));
  };

  const switchTurn = () => {
    const next = currentTurn === 1 ? 2 : 1;
    setCurrentTurn(next);
    localStorage.setItem("hujjah_turn", String(next));
  };

  // Manual turn override — set to specific team
  const setTurn = (team) => {
    setCurrentTurn(team);
    localStorage.setItem("hujjah_turn", String(team));
  };

  const resetTurn = () => {
    setCurrentTurn(1);
    localStorage.setItem("hujjah_turn", "1");
  };

  const saveSession = (s) => {
    setSession(s);
    if (s) {
      localStorage.setItem("hujjah_session", JSON.stringify(s));
      setTeamScores({ team1: s.team1_score ?? 0, team2: s.team2_score ?? 0 });
    } else {
      localStorage.removeItem("hujjah_session");
    }
  };

  const loginUser = async (identifier, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { identifier, password }, {
      headers: { "X-Device-Id": deviceId },
    });
    setCurrentUser(data.user);
    setUserToken(data.token);
    localStorage.setItem("hujjah_user", JSON.stringify(data.user));
    localStorage.setItem("hujjah_user_token", data.token);
    if (data.session_id)  localStorage.setItem("hujjah_session_id", data.session_id);
    if (data.device_id)   localStorage.setItem("hujjah_device_id",  data.device_id);
    if (data.user?.id) identifyUser(data.user.id, { username: data.user.username });
    return data;
  };

  const registerUser = async (email, username, password) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, username, password }, {
      headers: { "X-Device-Id": deviceId },
    });
    setCurrentUser(data.user);
    setUserToken(data.token);
    localStorage.setItem("hujjah_user", JSON.stringify(data.user));
    localStorage.setItem("hujjah_user_token", data.token);
    if (data.session_id)  localStorage.setItem("hujjah_session_id", data.session_id);
    if (data.device_id)   localStorage.setItem("hujjah_device_id",  data.device_id);
    if (data.user?.id) identifyUser(data.user.id, { username: data.user.username });
    return data;
  };

  const loginWithApple = async (appleResponse) => {
    const { data } = await axios.post(`${API}/auth/apple`, {
      id_token:   appleResponse.authorization?.id_token,
      first_name: appleResponse.user?.name?.firstName || "",
      last_name:  appleResponse.user?.name?.lastName  || "",
    }, { headers: { "X-Device-Id": deviceId } });
    setCurrentUser(data.user);
    setUserToken(data.token);
    localStorage.setItem("hujjah_user", JSON.stringify(data.user));
    localStorage.setItem("hujjah_user_token", data.token);
    if (data.session_id) localStorage.setItem("hujjah_session_id", data.session_id);
    if (data.device_id)  localStorage.setItem("hujjah_device_id",  data.device_id);
    if (data.user?.id) identifyUser(data.user.id, { username: data.user.username });
    return data;
  };

  const loginWithGoogle = async (credentialResponse) => {
    const { data } = await axios.post(`${API}/auth/google`, {
      access_token: credentialResponse.access_token,
      credential: credentialResponse.credential,
    }, { headers: { "X-Device-Id": deviceId } });
    setCurrentUser(data.user);
    setUserToken(data.token);
    localStorage.setItem("hujjah_user", JSON.stringify(data.user));
    localStorage.setItem("hujjah_user_token", data.token);
    if (data.session_id) localStorage.setItem("hujjah_session_id", data.session_id);
    if (data.device_id)  localStorage.setItem("hujjah_device_id",  data.device_id);
    if (data.user?.id) identifyUser(data.user.id, { username: data.user.username });
    return data;
  };

  const loginAsGuest = () => {
    const guest = { id: null, username: "ضيف", subscription_type: "free", is_guest: true };
    setCurrentUser(guest);
    // Do NOT persist to localStorage — guest session is memory-only
  };

  const logoutUser = () => {
    setCurrentUser(null);
    setUserToken(null);
    localStorage.removeItem("hujjah_user");
    localStorage.removeItem("hujjah_user_token");
    localStorage.removeItem("hujjah_session_id");
    resetAnalyticsUser();
  };

  const refreshUser = async () => {
    if (!userToken) return;
    try {
      const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${userToken}` } });
      setCurrentUser(data);
      localStorage.setItem("hujjah_user", JSON.stringify(data));
    } catch { /* silent — avoid false logouts on transient network errors */ }
  };

  const createSession = async (team1_name, team2_name, game_mode) => {
    setLoading(true);
    try {
      const payload = { team1_name, team2_name };
      if (game_mode) payload.game_mode = game_mode;
      if (currentUser?.id) payload.user_id = currentUser.id;
      const headers = userToken ? { Authorization: `Bearer ${userToken}` } : {};
      const { data } = await axios.post(`${API}/game/session`, payload, { headers });
      saveSession(data);
      setSelectedQuestions(new Set());
      // Reset lifelines for new session
      setLifelinesState(DEFAULT_LIFELINES);
      if (data.id) localStorage.removeItem(`lifelines_${data.id}`);
      return data;
    } finally {
      setLoading(false);
    }
  };

  const updateSession = async (updates) => {
    if (!session?.id) return;
    try {
      const headers = userToken ? { Authorization: `Bearer ${userToken}` } : {};
      const { data } = await axios.put(`${API}/game/session/${session.id}`, updates, { headers });
      saveSession(data);
      return data;
    } catch (e) {
      console.error("Session update error", e);
      throw e;
    }
  };

  const getNextQuestion = async (category_id, difficulty) => {
    if (!session?.id) return null;
    const headers = userToken ? { Authorization: `Bearer ${userToken}` } : {};
    try {
      const { data } = await axios.post(
        `${API}/game/session/${session.id}/question?category_id=${category_id}&difficulty=${difficulty}`,
        {},
        { headers }
      );
      setCurrentQuestion(data);
      return data;
    } catch (e) {
      console.error("Get question error", e);
      return null;
    }
  };

  const updateScore = async (team, points, question_id) => {
    if (!session?.id) return;
    const payload = { team, points };
    if (question_id) payload.question_id = question_id;
    try {
      const { data } = await axios.post(`${API}/game/session/${session.id}/score`, payload);
      const updated = { ...session, team1_score: data.team1_score, team2_score: data.team2_score };
      saveSession(updated);
      setTeamScores({ team1: data.team1_score, team2: data.team2_score });
      return data;
    } catch (e) {
      console.error("Score update error", e);
      throw e;
    }
  };

  // Set a team's score to an exact value (Game Master live edit)
  const setExactScore = async (team, value) => {
    const current = team === 1 ? teamScores.team1 : teamScores.team2;
    const delta = value - current;
    if (delta === 0) return;
    return await updateScore(team, delta);
  };

  // Adjust score with a signed delta string like "+300" or "-200"
  const adjustScoreDelta = async (team, deltaStr) => {
    const delta = parseInt(deltaStr, 10);
    if (isNaN(delta)) return { error: "قيمة غير صالحة" };
    return await updateScore(team, delta);
  };

  const resetGame = () => {
    const sid = sessionIdRef.current;
    if (sid) {
      localStorage.removeItem(`lifelines_${sid}`);
      localStorage.removeItem(`save_id_${sid}`);
      localStorage.removeItem(`save_version_${sid}`);
      localStorage.removeItem(`categories_${sid}`);
      localStorage.removeItem(`manifest_${sid}`);
    }
    setLifelinesState(DEFAULT_LIFELINES);
    saveSession(null);
    resetTurn();
    setCurrentQuestion(null);
    setRemainingTime(null);
    setSelectedQuestions(new Set());
    setTeamScores({ team1: 0, team2: 0 });
    setSaveStatus("idle");
    localStorage.removeItem("hujjah_multi_used");
  };

  // ── Game Save / Resume system ──────────────────────────────────────────────
  const [saveStatus, setSaveStatus] = useState("idle"); // "idle"|"saving"|"saved"|"error"

  const saveGameSnapshot = useCallback(async (snapshot) => {
    if (!userToken) return null;
    const sid = snapshot.session_id;
    const headers = { Authorization: `Bearer ${userToken}` };
    setSaveStatus("saving");

    const attemptSave = async (version) => {
      const { data } = await axios.post(`${API}/game/saves`, {
        ...snapshot,
        version,
        device_id: deviceId,
      }, { headers });
      if (sid) {
        localStorage.setItem(`save_id_${sid}`, data.id);
        localStorage.setItem(`save_version_${sid}`, String(data.version));
      }
      return data;
    };

    try {
      const storedVersion = parseInt(localStorage.getItem(`save_version_${sid}`) || "0");
      const data = await attemptSave(storedVersion);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(s => s === "saved" ? "idle" : s), 3000);
      return data;
    } catch (err) {
      const status = err?.response?.status;

      // 401 = token expired or invalid — clear stale auth, ask user to re-login
      if (status === 401) {
        setUserToken(null);
        setCurrentUser(null);
        localStorage.removeItem("hujjah_user_token");
        localStorage.removeItem("hujjah_user");
        setSaveStatus("error");
        setTimeout(() => setSaveStatus(s => s === "error" ? "idle" : s), 5000);
        return { _authExpired: true };
      }

      // 409 = version conflict (localStorage out of sync, e.g. Safari ITP cleared storage).
      // Recover real version from server and retry once.
      if (status === 409 && sid) {
        try {
          const { data: saves } = await axios.get(`${API}/game/saves`, { headers });
          const match = saves.find(s => s.session_id === sid);
          if (match) {
            localStorage.setItem(`save_version_${sid}`, String(match.version));
            if (match.id) localStorage.setItem(`save_id_${sid}`, match.id);
            const data = await attemptSave(match.version);
            setSaveStatus("saved");
            setTimeout(() => setSaveStatus(s => s === "saved" ? "idle" : s), 3000);
            return data;
          }
        } catch {}
      }
      // Final failure — cache locally
      if (sid) {
        try { localStorage.setItem(`offline_save_${sid}`, JSON.stringify({ ...snapshot, _offline: true, _ts: Date.now() })); } catch {}
      }
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(s => s === "error" ? "idle" : s), 5000);
      return null;
    }
  }, [userToken, deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Restore a saved game into all context state + localStorage ─────────────
  // Must be called instead of manual localStorage writes so React state stays
  // in sync (selectedQuestions Set and lifelinesState are NOT re-read from
  // localStorage after mount).
  const restoreGameState = useCallback((save) => {
    const restoredSession = {
      id:               save.session_id,
      team1_name:       save.team1_name  || "",
      team2_name:       save.team2_name  || "",
      team1_score:      save.team1_score || 0,
      team2_score:      save.team2_score || 0,
      team1_categories: (save.categories || []).map(c => c.id),
      team2_categories: [],
      used_questions:   save.used_question_ids || [],
      is_trial:         false,
      status:           "active",
    };
    // Restore solo setup if this is a solo save
    if (save.game_mode === "solo" && save.solo_difficulty) {
      const restoredSolo = {
        playerName: save.solo_player_name || save.team1_name || "",
        difficulty: save.solo_difficulty,
        target: save.solo_target,
      };
      setSoloSetupState(restoredSolo);
      localStorage.setItem("hujjah_solo_setup", JSON.stringify(restoredSolo));
    }

    // Update React state ─ this is what the UI actually reads
    saveSession(restoredSession);
    setSelectedQuestions(new Set(save.used_tiles || []));
    setLifelinesState(save.lifelines || DEFAULT_LIFELINES);
    setCurrentTurn(save.current_turn || 1);

    // Persist to localStorage as secondary source (for page reloads / QuestionPage)
    const sid = save.session_id;
    if (sid) {
      localStorage.setItem(`used_${sid}`,        JSON.stringify(save.used_tiles || []));
      localStorage.setItem(`lifelines_${sid}`,   JSON.stringify(save.lifelines || DEFAULT_LIFELINES));
      localStorage.setItem(`categories_${sid}`,  JSON.stringify(save.categories || []));
      localStorage.setItem(`save_id_${sid}`,     save.id);
      localStorage.setItem(`save_version_${sid}`, String(save.version || 1));
    }
    localStorage.setItem("hujjah_turn", String(save.current_turn || 1));

    // Clear stale manifest so PreparationScreen re-fetches from server (idempotent)
    setManifest(null);
    if (sid) localStorage.removeItem(`manifest_${sid}`);

    return restoredSession;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const listGameSaves = useCallback(async () => {
    if (!userToken) return [];
    try {
      const { data } = await axios.get(`${API}/game/saves`, { headers: { Authorization: `Bearer ${userToken}` } });
      return data;
    } catch { return []; }
  }, [userToken]);

  const deleteGameSave = useCallback(async (saveId) => {
    if (!userToken) return;
    await axios.delete(`${API}/game/saves/${saveId}`, { headers: { Authorization: `Bearer ${userToken}` } });
  }, [userToken]);

  const resumeGameSave = useCallback(async (saveId) => {
    if (!userToken) return null;
    const { data } = await axios.post(
      `${API}/game/saves/${saveId}/resume`,
      { device_id: deviceId },
      { headers: { Authorization: `Bearer ${userToken}` } },
    );
    return data;
  }, [userToken, deviceId]);

  const completeGameSave = useCallback(async (saveId, finalScores) => {
    if (!userToken || !saveId) return;
    try {
      await axios.post(
        `${API}/game/saves/${saveId}/complete`,
        finalScores || {},
        { headers: { Authorization: `Bearer ${userToken}` } },
      );
    } catch {}
  }, [userToken]);

  // ── Offline sync: when internet returns, flush any locally cached saves ──
  useEffect(() => {
    const syncOfflineSaves = async () => {
      if (!userToken) return;
      const keys = Object.keys(localStorage).filter(k => k.startsWith("offline_save_"));
      for (const key of keys) {
        try {
          const raw = localStorage.getItem(key);
          if (!raw) continue;
          const snapshot = JSON.parse(raw);
          const { _offline, _ts, ...body } = snapshot;
          const sid = body.session_id;
          const storedVersion = parseInt(localStorage.getItem(`save_version_${sid}`) || "0");
          const { data } = await axios.post(
            `${API}/game/saves`,
            { ...body, version: storedVersion, device_id: deviceId },
            { headers: { Authorization: `Bearer ${userToken}` } },
          );
          if (sid) {
            localStorage.setItem(`save_id_${sid}`, data.id);
            localStorage.setItem(`save_version_${sid}`, String(data.version));
          }
          localStorage.removeItem(key);
        } catch {}
      }
    };
    window.addEventListener("online", syncOfflineSaves);
    return () => window.removeEventListener("online", syncOfflineSaves);
  }, [userToken, deviceId]);

  // Central premium check — single source of truth for all pages
  // A user is premium if: subscription_type is "premium" or "paid", OR has active game credits
  const isPremium = (() => {
    if (!currentUser) return false;
    if (currentUser.is_premium === true) return true;
    if (currentUser.subscription_type === "premium" || currentUser.subscription_type === "paid") return true;
    const credits = currentUser.game_credits || 0;
    if (credits <= 0) return false;
    const exp = currentUser.credits_expires_at;
    if (exp) {
      try { if (new Date(exp) < new Date()) return false; } catch {}
    }
    return true;
  })();

  return (
    <GameContext.Provider value={{
      session, loading, currentUser, setCurrentUser, userToken, darkMode, gameSettings, currentTurn,
      currentQuestion, setCurrentQuestion,
      remainingTime, setRemainingTime,
      selectedQuestions, markTileUsed, isTileUsed, restoreTile,
      manifest, saveManifest,
      teamScores,
      gameMode, setGameMode,
      tournamentState, setTournament, updateTournament,
      soloSetup, setSoloSetup,
      createSession, updateSession, getNextQuestion, updateScore, setExactScore, adjustScoreDelta,
      resetGame, saveSession, loginUser, registerUser, logoutUser, loginAsGuest, loginWithGoogle, loginWithApple, refreshUser,
      toggleDarkMode, switchTurn, setTurn, resetTurn,
      isPremium,
      lifelinesState, consumeLifeline,
      deviceId,
      saveStatus, saveGameSnapshot, listGameSaves, deleteGameSave, resumeGameSave, completeGameSave,
      restoreGameState,
    }}>
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame must be used within GameProvider");
  return ctx;
};

export default GameContext;
